'use client'

import { sendClientRequest } from './client-request'
import { uploadFiles } from './request'
import type { Session } from './types'

/**
 * The dashboard's view of the API.
 *
 * Every shape here mirrors a `present*` function in the backend, which is the
 * only thing that decides what leaves the server. When one of those changes,
 * this is the file that has to change with it.
 */

export type BlockType =
  | 'link' | 'social' | 'text' | 'image' | 'video' | 'audio'
  | 'product' | 'booking' | 'ticket' | 'donate' | 'email' | 'map'
  | 'poll' | 'countdown' | 'wishlist' | 'cv' | 'qa' | 'pet'
  | 'heading' | 'divider' | 'qr' | 'hours'
  | 'bank' | 'chat' | 'promo'
  | 'blog'

export type BlockGroup = 'basic' | 'media' | 'commerce' | 'support' | 'interactive' | 'layout'

/**
 * How a surface is painted, as opposed to what it says.
 *
 * Kept apart from `BlockContent` because it is orthogonal to type: a colour
 * means the same thing on a link block, on a ticket block and on the page all
 * of them sit on, whereas content is validated by a different schema for each
 * of the twelve types. Mirrors `surfaceStyle` in the backend's lib/surface.js,
 * which is the copy that decides.
 *
 * Keys belonging to a fill other than the active one are kept rather than
 * cleared, so that trying a gradient, switching to a photo and switching back
 * does not mean retyping two colours.
 */
export interface SurfaceStyle {
  fill?: 'none' | 'solid' | 'gradient' | 'image'
  /** Solid fill. */
  color?: string
  /** Gradient ends, and its direction in degrees clockwise from up. */
  from?: string
  to?: string
  angle?: number
  /** Background photo, as the bucket filename `/upload` answered with. */
  image?: string
  blur?: number
  /** Overrides the colour every piece of text on the card is drawn in. */
  text?: string
}

/** Presentation fields. Anything sellable is referenced, never copied in here. */
export interface BlockContent {
  title?: string
  sub?: string
  url?: string
  brand?: string
  cta?: string
  amounts?: number[]
  images?: string[]
  /** How an image block's picture sits in its cell. Absent means `cover`. */
  fit?: ImageFit
  /** Where a text block's words sit in their cell. Absent means `top`. */
  alignY?: TextAlignY
  /** Where a media block's title sits. Absent means the type's own default. */
  caption?: CaptionMode
  coords?: { lat: number, lng: number }
  /**
   * Which of a product's photographs fronts this block, by index.
   *
   * Absent means the first, which is the gallery's own cover. The block can
   * differ from it because the gallery order belongs to the product and the
   * card is one placement of that product among several.
   */
  cover?: number
  /** A poll's answers. Ids are stable across edits, because votes point at them. */
  options?: PollOption[]
  /** When a poll stops taking votes, or a promo code stops working, ISO 8601. Absent means never. */
  endsAt?: string
  /** Whether a poll's numbers show before a visitor votes. Absent means `vote`. */
  results?: 'vote' | 'always'
  /** The moment a countdown reaches zero, ISO 8601. */
  target?: string
  /** What a countdown says once it reaches zero. */
  done?: string
  /** How a countdown draws its time (absent is `digits`), or how a divider draws itself (absent is `line`). */
  look?: CountdownLook | DividerLook
  /** A heading's horizontal alignment. Absent is `left`. */
  align?: HeadingAlign
  /** A heading's type size. Absent is `md`. */
  textSize?: HeadingSize
  /** Whether a heading draws a hairline beside its title. */
  rule?: boolean
  /** What a QR block's code holds (absent is `url`), or what a blog block shows (absent is `post`). */
  mode?: QrMode | BlogBlockMode
  /** The post a blog block shows in `post` mode. Checked against the owner's own posts on save. */
  postId?: string
  /** How many posts a blog block lists in `list` mode. Absent is 3. */
  count?: number
  /** A QR block's network, in `wifi` mode only. */
  wifi?: WifiNetwork
  /** A QR block's contact card, in `contact` mode only. */
  contact?: QrContact
  /** An hours block's week, Sunday first (index 0), in the business's UTC+8 clock. */
  week?: TimeRange[][]
  /** An hours block's dated departures from the week. */
  exceptions?: HoursException[]
  /** A bank block's accounts, in the owner's order. */
  accounts?: BankAccount[]
  /** A quick-contact block's buttons, in the owner's order. The first leads on a one-row card. */
  channels?: ChatChannel[]
  /** Text a quick-contact block pre-fills where the service allows it (Messenger, WhatsApp, SMS). */
  message?: string
  /** A promo block's code, exactly as typed. */
  code?: string
  /** A promo block's brand picture, as the bucket filename `/upload` answered with. */
  logo?: string
  /** A wishlist's gifts. */
  items?: WishlistItem[]
  /** Whether visitors may reserve wishlist items. Absent means yes. */
  claims?: boolean
  /** A CV's lines, each tagged with the heading it belongs under. */
  entries?: CvEntry[]
  skills?: string[]
  /** What a CV's owner is looking for right now. Absent means no status pill. */
  seeking?: CvSeeking
  /** A PDF the owner uploaded to their CV. Either, both or neither of this and `url`. */
  pdf?: CvPdf
  /** A Q&A block that has stopped taking new questions. Its published answers stay. */
  closed?: boolean
  /** A Q&A block's preset: colours, default prompt, starter questions. Absent is `ask`. */
  vibe?: 'ask' | 'confess' | 'rate' | 'crush' | 'song' | 'honest'
  /** A pet's kind. Absent is `mochi`, the free one. */
  species?: PetSpecies
  /** A pet's colour. Absent is `peach`. */
  color?: PetColor
  /** What a pet wears. Paid extras. */
  accessories?: PetAccessory[]
  /** What a pet sits in front of. A paid extra. */
  scene?: PetScene
}

/**
 * What a blog block shows: one chosen post, whichever post is newest, or a
 * short list of the newest. Mirrors BLOG_BLOCK_MODES in the backend's
 * lib/block-catalog.js.
 */
export type BlogBlockMode = 'post' | 'latest' | 'list'

/** Where a post stands. `changed` is live with draft edits readers do not see yet. */
export type BlogPostStatus = 'draft' | 'live' | 'changed'

/**
 * A published post as a card shows it. Never a draft: the backend attaches
 * only live copies to a page, so nothing here can leak an unfinished sentence.
 */
export interface BlogPostSummary {
  id: string
  slug: string
  title: string
  excerpt: string | null
  /** Bucket filename, like every other stored picture. `formatImageURL` expands it. */
  cover: string | null
  publishedAt: string
  readMinutes: number
}

/**
 * What a blog block carries next to its content, joined by the page endpoints.
 *
 * `posts` is what the card draws, newest first; `post` mode holds at most one.
 * `pending` reaches the owner's editor only: the post a `post` mode block
 * points at when visitors cannot see it, so the card can say why it is blank
 * on the page instead of looking broken.
 */
export interface BlogBlockData {
  posts: BlogPostSummary[]
  pending?: { status: 'draft', id: string, title: string } | { status: 'missing', id: string | null, title: null }
}

/** Mirror PET_SPECIES and friends in the backend's lib/pet.js. */
export type PetSpecies = 'mochi' | 'cat' | 'bunny' | 'dragon' | 'cloud'
export type PetColor = 'peach' | 'mint' | 'lilac' | 'gold' | 'holo'
export type PetAccessory = 'crown' | 'scarf'
export type PetScene = 'night' | 'meadow' | 'sunset'
export type PetAction = 'feed' | 'pet' | 'play'
export type PetMood = 'happy' | 'hungry' | 'sleepy' | 'bored' | 'sick'
export type PetStage = 'egg' | 'baby' | 'teen' | 'adult'

/** What a pet wears right now: its saved look, less any extras the owner's plan no longer covers. */
export interface PetLook {
  species: PetSpecies
  color: PetColor
  accessories: PetAccessory[]
  scene: PetScene | null
  /** Saved extras are not being drawn because the plan does not cover them. */
  locked: boolean
}

/** A pet as it is right now. Mirrors `presentPet` in the backend's lib/pet.js. */
export interface PetState {
  stage: PetStage
  mood: PetMood
  /** How an adult turned out. Null before it grows up. */
  temper: 'loved' | 'grumpy' | null
  /** Days since it hatched. */
  day: number
  food: number
  fun: number
  energy: number
  /** Visitors who helped it today. */
  helpersToday: number
  look: PetLook
}

/** The pet as one visitor sees it, with what they already did today. */
export interface PetVisitorState extends PetState {
  acted: PetAction | null
}

export interface PollOption {
  id: string
  label: string
}

export interface WishlistItem {
  id: string
  name: string
  url?: string
  price?: number
  /** Bucket filename, as `/upload` answered with. */
  image?: string
  note?: string
}

/** The faces a countdown can wear. Mirrors COUNTDOWN_LOOKS in the backend's block-catalog.js. */
export type CountdownLook = 'digits' | 'flip' | 'rings' | 'spotlight'

/** Mirrors HEADING_ALIGNS in the backend's block-catalog.js. */
export type HeadingAlign = 'left' | 'center' | 'right'
/** Mirrors HEADING_SIZES. */
export type HeadingSize = 'sm' | 'md' | 'lg'
/** Mirrors DIVIDER_LOOKS. `space` draws nothing: the spacer. */
export type DividerLook = 'line' | 'dots' | 'wave' | 'space'
/** Mirrors QR_MODES. */
export type QrMode = 'url' | 'wifi' | 'contact'
/** Mirrors WIFI_SECURITY. */
export type WifiSecurity = 'WPA' | 'WEP' | 'nopass'

export interface WifiNetwork {
  ssid: string
  password?: string
  /** Absent is `WPA`. */
  security?: WifiSecurity
  hidden?: boolean
}

export interface QrContact {
  name: string
  phone?: string
  email?: string
  org?: string
  /** Job title, vCard TITLE. */
  role?: string
}

/** Mirrors BANK_KEYS in the backend's block-catalog.js. `other` names itself in `bankName`. */
export type BankKey = 'khan' | 'golomt' | 'tdb' | 'state' | 'xac' | 'capitron' | 'bogd' | 'arig' | 'chinggis' | 'mbank' | 'transport' | 'nibank' | 'other'

export interface BankAccount {
  id: string
  bank: BankKey
  /** Only for `other`. */
  bankName?: string
  /** Digits only, 5 to 16. At least one of this and `iban`. */
  number?: string
  /** `MN` and 18 digits, no spaces. */
  iban?: string
  holder: string
  note?: string
}

/** Mirrors CHAT_KINDS. */
export type ChatKind = 'messenger' | 'telegram' | 'whatsapp' | 'viber' | 'instagram' | 'phone' | 'sms'

export interface ChatChannel {
  id: string
  kind: ChatKind
  /** A handle without its @, or a phone number as + and digits. Never a URL: links are built from it. */
  value: string
}

/** A date that departs from the week. No ranges is closed all day. */
export interface HoursException {
  id: string
  /** YYYY-MM-DD in the business's UTC+8 calendar. */
  date: string
  ranges: TimeRange[]
  note?: string
}

export type CvKind = 'education' | 'experience' | 'award' | 'activity'

/** Mirrors CV_SEEKING in the backend's block-catalog.js. */
export type CvSeeking = 'intern' | 'job' | 'freelance' | 'scholarship'

export interface CvEntry {
  id: string
  kind: CvKind
  title: string
  org?: string
  period?: string
  note?: string
}

/** A poll's numbers. Null counts mean the visitor has to vote first. */
export interface PollState {
  closed: boolean
  myVote: string | null
  total: number
  counts: Record<string, number> | null
}

/**
 * Crop to fill the card, or shrink to show the whole picture.
 *
 * A photograph wants `cover`; a logo or a screenshot loses its meaning when its
 * edges are cut off, so it wants `contain`.
 */
export type ImageFit = 'cover' | 'contain'

/**
 * Which end of a text block its words sit at.
 *
 * A block is a fixed cell, so anything shorter than the cell leaves space. Where
 * that space goes is the owner's call: a heading centred in its box and a
 * caption pinned under one are different designs, not different amounts of text.
 */
export type TextAlignY = 'top' | 'center' | 'bottom'

/**
 * Where a media block's title sits: under the picture, over it, or nowhere.
 *
 * Neither of the first two is right for every block, because the picture is
 * somebody else's: text over a clean frame gives the media the whole card, and
 * the same text over a busy one is a harder read than a plain strip.
 */
export type CaptionMode = 'below' | 'over' | 'hidden'

/** A span on the grid. Desktop is four columns wide, a phone two. */
export interface BlockSize { w: number, h: number }

/**
 * A resize, naming the device it applies to.
 *
 * Two disjoint shapes rather than four optional numbers, because a resize is
 * always one device or the other and a patch carrying both would mean the
 * caller had guessed at the pair it was not editing. A null in the phone pair
 * clears the override and hands the block back to the default rule.
 */
export type BlockSizePatch =
  | { w: number, h: number }
  | { mobileW: number | null, mobileH: number | null }

/**
 * A staff takedown on a block or page, as the owner is told about it. Null
 * (or absent) when none is in force. The owner can still edit, but cannot
 * bring the item back themselves.
 */
export interface Moderation {
  disabledAt: string
  disabledUntil: string | null
  reason: 'scam' | 'prohibited' | 'copyright' | 'impersonation' | 'spam' | 'adult' | 'other'
}

export interface Block {
  id: string
  type: BlockType
  sortOrder: number
  w: number
  h: number
  /**
   * The phone override, or null to follow the default rule. Null and a number
   * that happens to match are not the same thing: null tracks the rule if the
   * rule changes, a number does not.
   */
  mobileW: number | null
  mobileH: number | null
  /** What the override resolves to. Derived by the backend, never posted back. */
  mobile: BlockSize
  /**
   * Forces the block to begin a row rather than fill the gap on the one above.
   * The one piece of placement the grid stores, and it holds in both layouts.
   */
  startsNewRow: boolean
  visible: boolean
  /**
   * The block's window on the public page, ISO 8601 UTC. Null is no bound on
   * that side. Optional because an older server does not send them. Visitors
   * never receive these: the server leaves out blocks outside their window
   * and strips the fields from the rest.
   */
  showFrom?: string | null
  showUntil?: string | null
  moderation?: Moderation | null
  content: BlockContent
  /** How the card is painted. An empty object is "follow the page theme". */
  style: SurfaceStyle
  productId: string | null
  serviceId: string | null
  eventId: string | null
  /**
   * The product this block points at, joined by the editor endpoint.
   *
   * Present so the editor can draw the real card rather than a placeholder.
   * Optional because a block whose product was deleted still comes back, and
   * because only product blocks carry one. Same shape the public page reads,
   * stock included as a boolean rather than a count.
   */
  product?: {
    id: string
    name: string
    description: string | null
    /** What the buyer is charged, sale already applied. */
    price: number
    /** The struck-through original, present only while a sale is running. */
    listPrice: number | null
    images: string[]
    inStock: boolean
    isDigital: boolean
    digitalName: string | null
    digitalSize: number | null
  }
  /**
   * The service a booking block points at, joined by the same endpoint and for
   * the same reason: without it the editor can only draw a name, where the
   * visitor's card shows the duration and price too.
   */
  service?: {
    id: string
    name: string
    description: string | null
    durationMinutes: number
    price: number
  }
  /**
   * The event a ticket block points at, joined by the same endpoint and for the
   * same reason as the two above: without it the editor draws a bare name where
   * the visitor's card shows the date, the venue and the price.
   */
  event?: {
    id: string
    name: string
    description: string | null
    venue: string | null
    coverUrl: string | null
    startsAt: string
    endsAt: string | null
    fromPrice: number | null
    hasTickets: boolean
  }
  /** A poll block's live counts, joined for the owner. Always visible to them. */
  poll?: { counts: Record<string, number>, total: number, closed: boolean }
  /** Which of a wishlist block's items are reserved, joined for the owner. */
  wishlist?: { claimed: string[] }
  /** How many questions sent through a Q&A block are still unanswered. */
  qa?: { unanswered: number }
  /** A pet block's live state, joined by the page endpoints. */
  pet?: PetState
  /** A blog block's posts, joined by the page endpoints. See `BlogBlockData`. */
  blog?: BlogBlockData
}

/** What the config modal saves for a block's schedule. Both keys always sent; null clears. */
export interface BlockSchedule {
  showFrom: string | null
  showUntil: string | null
}

/** An anonymous question in the owner's inbox. Nothing about the sender is here, by design. */
export interface Question {
  id: string
  blockId: string | null
  body: string
  answer: string | null
  answeredAt: string | null
  /** Shown on the block. Only an answered question can be. */
  publishedAt: string | null
  createdAt: string
}

export type QuestionFilter = 'new' | 'answered' | 'all'

export type QuestionReportReason = 'bullying' | 'threat' | 'sexual' | 'hate' | 'spam' | 'other'

/** A question and its answer, as visitors see them on the block. */
export interface PublishedAnswer {
  id: string
  body: string
  answer: string
  answeredAt: string
}

export interface PageState {
  id: string
  slug: string | null
  title: string | null
  bio: string | null
  avatarUrl: string | null
  coverUrl: string | null
  theme: string
  /**
   * What the page itself is painted with. Empty is "whatever the theme says".
   *
   * Alongside `theme` rather than instead of it: the theme still decides what
   * the cards, borders and muted text look like, and this decides what they
   * sit on.
   */
  background: SurfaceStyle
  isPublished: boolean
  isPrimary: boolean
  moderation?: Moderation | null
}

export interface SocialLink {
  id?: string
  brand: string
  url: string
  sortOrder?: number
}

/** Mirrors the boolean and limit columns on the Plan row. */
export interface Entitlements {
  planId: string
  planSlug: string
  planName: string
  subscription: {
    id: string
    months: number
    /** What was charged, after any coupon. `discount` is what came off. */
    amount: number
    discount: number
    startsAt: string
    endsAt: string
    /**
     * `grace` means the term has ended but the features are still on while the
     * renewal is chased. `graceEndsAt` is when that stops.
     */
    status: 'pending' | 'active' | 'grace' | 'expired' | 'cancelled'
    graceEndsAt: string | null
    autoRenew: boolean
  } | null
  /** The renewal waiting to be paid, while in grace. Null at every other time. */
  renewalPaymentId: string | null
  statsRetentionDays: number | null
  canSellProducts: boolean
  canAcceptBookings: boolean
  canSellTickets: boolean
  canAcceptDonations: boolean
  canCollectEmails: boolean
  canUseThemes: boolean
  canUseCustomDomain: boolean
  canHideBranding: boolean
  canViewFullStats: boolean
  canExportData: boolean
  canInviteTeam: boolean
  canUsePremiumPets: boolean
  maxBlocks: number | null
  maxPages: number | null
  maxProducts: number | null
  /** Blog posts the account may own, drafts included. Null is unlimited. */
  maxPosts: number | null
  /**
   * Largest digital product file, in MB. Null means the plan cannot sell
   * digital products at all -- the opposite of what null means on the other
   * limits here, where it is "unlimited".
   */
  maxDigitalFileMb: number | null
  maxTeamMembers: number | null
}

/** A plan flag name, so a catalog entry's gate is checked rather than guessed. */
export type FeatureFlag = Extract<keyof Entitlements, `can${string}`>

export interface CatalogEntry {
  type: BlockType
  group: BlockGroup
  defaultSize: { w: number, h: number }
  requires: FeatureFlag | null
  /** Adding one needs ДАН verification. Absent from an older server, which reads as no. */
  requiresDan?: boolean
  reference: 'productId' | 'serviceId' | 'eventId' | null
}

export interface EditorState {
  page: PageState
  blocks: Block[]
  socialLinks: SocialLink[]
  entitlements: Entitlements
}

export interface Product {
  id: string
  name: string
  sku: string | null
  description: string | null
  price: number
  /** Below `price` while a sale is running; null otherwise. */
  salePrice: number | null
  stock: number
  status: 'draft' | 'published' | 'soldout'
  images: string[]
  /** A file the buyer downloads after paying. The storage path never leaves the server. */
  isDigital: boolean
  digitalName: string | null
  digitalSize: number | null
  hasShipping: boolean
  hasPickup: boolean
  shippingFee: number
  pickupAddress: string | null
  pickupNote: string | null
  maxDownloads: number
}

/** What the upload endpoint hands back, passed straight to create/update. */
/** A CV's uploaded PDF, as `/upload/cv-pdf` answers and the block stores it. */
export interface CvPdf {
  /** Bucket filename under `uploads/`; formatImageURL turns it into a link. */
  file: string
  /** The name it had on the owner's computer, for the editor's chip. */
  name: string
  size: number
}

export interface DigitalFile {
  path: string
  name: string
  size: number
  mimeType: string
}

export interface OrderItem { id: string, productId: string | null, name: string, price: number, quantity: number }

export interface Order {
  id: string
  reference: string
  buyerName: string | null
  buyerPhone: string | null
  buyerEmail: string | null
  deliveryAddress: string | null
  note: string | null
  total: number
  status: 'pending' | 'paid' | 'shipped' | 'delivered' | 'cancelled' | 'refunded'
  paidAt: string | null
  createdAt: string
  items: OrderItem[]
  /**
   * Delivery state for the digital lines, on the detail view only.
   *
   * Empty on a physical order, and empty in the list, which loads items but not
   * grants. The claim never appears here: it is emailed once and only its hash
   * is stored, so what a seller gets is whether the buyer collected the file
   * and how many goes remain.
   */
  downloads?: Array<{
    id: string
    productId: string
    downloads: number
    total: number
    lastDownloadAt: string | null
  }>
}

/**
 * Something a visitor can book time for.
 *
 * The record a booking block points at, in the same way a product block points
 * at a product. Distinct from a `Booking`, which is one appointment somebody
 * actually made: this is the offer, that is the result of it.
 */
export interface Service {
  id: string
  name: string
  description: string | null
  /** How long one appointment runs. Drives the slot grid a visitor picks from. */
  durationMinutes: number
  price: number
  /** How far ahead of a slot it stops being bookable. */
  leadTimeMinutes: number
  /** How far into the future the calendar opens. */
  bookingWindowDays: number
  /** Off keeps it out of the slot list without deleting its history. */
  active: boolean
  /**
   * Whether any opening hours are set. Only on the list response.
   *
   * A service without them offers no slots at all, so its booking block shows
   * an empty calendar. The list surfaces it because that state is invisible
   * otherwise and reads as a bug rather than as unfinished setup.
   */
  hasHours?: boolean
}

/** One open range of a day. Several per day is how a lunch break is expressed. */
export interface TimeRange {
  startMinute: number
  endMinute: number
}

export interface AvailabilityRule extends TimeRange {
  id?: string
  /** 0 Sunday through 6 Saturday. */
  weekday: number
}

/** A day that departs from the weekly rule: closed, or open with its own hours. */
export interface AvailabilityException {
  id: string
  /** YYYY-MM-DD in the owner's local calendar. */
  date: string
  isClosed: boolean
  intervals: TimeRange[]
}

/**
 * An open range too short to fit a whole slot.
 *
 * Not an error: closing an hour early is a legitimate choice. It is surfaced so
 * the seller knows the time will never sell, and can shorten the slot if that
 * was not what they meant.
 */
export interface ScheduleGap extends TimeRange {
  weekday: number
  minutes: number
  /** The whole range is shorter than one slot, so nothing there is bookable. */
  unusable: boolean
}

export interface Availability {
  rules: AvailabilityRule[]
  exceptions: AvailabilityException[]
  durationMinutes: number
  gaps: ScheduleGap[]
}

/** One appointment on the owner's own calendar, with the client attached. */
export interface CalendarBooking extends Booking {
  /** YYYY-MM-DD, precomputed by the server in the owner's local calendar. */
  day: string
  serviceName: string | null
}

export interface Booking {
  id: string
  reference: string
  serviceId: string
  clientName: string | null
  clientPhone: string | null
  clientEmail: string | null
  note: string | null
  startsAt: string
  endsAt: string
  price: number
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed'
  createdAt: string
}

export interface Donation {
  id: string
  amount: number
  message: string | null
  isAnonymous: boolean
  supporterName: string | null
  /**
   * Whether a receipt was sent, not where to.
   *
   * The address itself is deliberately absent from every response: a supporter
   * gives it so a receipt can reach them, which is not the same as giving it
   * to the page owner.
   */
  hasReceipt: boolean
  paidAt: string | null
  createdAt: string
}

/** What kind of gathering an event is. A label, not a rule. */
export type EventKind = 'conference' | 'workshop' | 'hackathon' | 'meetup' | 'other'

export const EVENT_KINDS: EventKind[] = ['conference', 'workshop', 'hackathon', 'meetup', 'other']

export interface FuseEvent {
  id: string
  name: string
  description: string | null
  venue: string | null
  coverUrl: string | null
  startsAt: string | null
  endsAt: string | null
  kind: EventKind
  /**
   * When tickets may be bought, which is not when the event runs. Either end
   * may be null, meaning unbounded on that side rather than closed.
   */
  registrationStartsAt: string | null
  registrationEndsAt: string | null
  status: 'draft' | 'published' | 'cancelled' | 'ended'
  createdAt: string
  updatedAt: string | null
}

/**
 * One tier on sale: VIP, Standard, Early bird, a group bundle.
 *
 * Tiers are rows rather than fixed columns, so a seller can name and price as
 * many as the event needs. `sold` is counted from the tickets rather than
 * stored, which is why it cannot drift from what was actually sold.
 */
export interface TicketType {
  id: string
  eventId: string
  name: string
  description: string | null
  price: number
  quantity: number
  sold: number
  remaining: number
  sortOrder: number
}

export interface Ticket {
  id: string
  eventId: string
  ticketTypeId: string
  code: string
  buyerName: string | null
  buyerPhone: string | null
  buyerEmail: string | null
  price: number
  status: 'pending' | 'valid' | 'used' | 'refunded' | 'void'
  checkedInAt: string | null
  createdAt: string
}

export interface Subscriber {
  id: string
  email: string
  source: string | null
  consentAt: string | null
  createdAt: string
}

export interface Payment {
  id: string
  kind: 'order' | 'booking' | 'ticket' | 'donation' | 'subscription'
  /**
   * The short handle printed on the QPay invoice, e.g. `S829182`.
   *
   * Null on rows opened before references existed, so anything showing it has
   * to cope with its absence rather than assume every payment has one.
   */
  reference: string | null
  amount: number
  status: 'pending' | 'paid' | 'failed' | 'refunded' | 'cancelled'
  paidAt: string | null
  settledAt: string | null
  createdAt: string
}

export interface StatsResponse {
  window: number
  retentionDays: number | null
  totals: { views: number, clicks: number, clickRate: number }
  daily: { day: string, views: number, clicks: number }[]
  blocks: { blockId: string, type: BlockType | null, title: string | null, views: number, clicks: number, clickRate: number }[]
}

/**
 * An API failure, carrying the backend's code so the caller can word it.
 *
 * `code` is the contract, never the prose: the same failure reads differently
 * on a catalog card and in a settings panel, and only the caller knows which.
 */
export interface ApiFailure {
  code: string
  error?: string
  /** Set on a plan.feature_locked failure: which can* flag was missing. */
  feature?: string
  /** Set on a plan.limit_reached failure: which max* limit, and its value. */
  limit?: string
  max?: number
  /** Set on a rejected password: every rule, and whether this password met it. */
  criteria?: PasswordCriterion[]
  // Index signature so this satisfies ApiErrorResponse and can go straight to
  // apiError() without a cast at every call site.
  [key: string]: unknown
}

export type ApiResult<T> = { ok: true, data: T } | { ok: false, failure: ApiFailure }

const UNREACHABLE: ApiFailure = { code: 'common.unknown' }

/**
 * One place every dashboard call goes through.
 *
 * `sendClientRequest` returns `false` on a transport error and a `{ status }`
 * envelope otherwise, which means every call site would otherwise repeat the
 * same three checks before it could trust what it has.
 */
async function call<T>(session: Session | null | undefined, url: string, method = 'GET', body?: object): Promise<ApiResult<T>> {
  const response = await sendClientRequest({
    url,
    method,
    body: body ?? {},
    session: session ?? undefined,
    auth: !!session,
  })

  if(!response || typeof response !== 'object') return { ok: false, failure: UNREACHABLE }
  if(response.status !== 200) {
    return { ok: false, failure: { code: response.code ?? 'common.unknown', error: response.error, feature: response.feature, limit: response.limit, max: response.max } }
  }
  return { ok: true, data: (response.data ?? response) as T }
}


/*  Editor  */

export const fetchEditorState = (session: Session) =>
  call<EditorState>(session, '/page')

export const fetchBlockCatalog = () =>
  call<{ maxWidth: number, maxHeight: number, types: CatalogEntry[] }>(null, '/blocks/catalog')

export const updatePage = (session: Session, pageId: string, patch: Partial<PageState>) =>
  call<PageState>(session, `/pages/${pageId}`, 'PATCH', patch)

export const saveSocialLinks = (session: Session, pageId: string, links: SocialLink[]) =>
  call<SocialLink[]>(session, `/pages/${pageId}/socials`, 'PUT', { links: links.map(({ brand, url }) => ({ brand, url })) })

export const addBlock = (session: Session, pageId: string, body: { type: BlockType, content?: BlockContent, productId?: string, serviceId?: string, eventId?: string }) =>
  call<Block>(session, `/pages/${pageId}/blocks`, 'POST', body)

export const patchBlock = (session: Session, blockId: string, patch: Partial<Pick<Block, 'w' | 'h' | 'mobileW' | 'mobileH' | 'startsNewRow' | 'visible' | 'content' | 'style' | 'productId' | 'serviceId' | 'eventId' | 'showFrom' | 'showUntil'>>) =>
  call<Block>(session, `/blocks/${blockId}`, 'PATCH', patch)

export const duplicateBlock = (session: Session, blockId: string) =>
  call<Block>(session, `/blocks/${blockId}/duplicate`, 'POST')

export const removeBlock = (session: Session, blockId: string) =>
  call<null>(session, `/blocks/${blockId}`, 'DELETE')

/** Sends the whole ordered id list, on drop rather than on every hover. */
export const saveBlockOrder = (session: Session, pageId: string, order: string[]) =>
  call<Block[]>(session, `/pages/${pageId}/blocks/order`, 'PATCH', { order })

export const undoBlockOrder = (session: Session, pageId: string) =>
  call<Block[]>(session, `/pages/${pageId}/blocks/undo`, 'POST')

/** The owner looking after their own pet. No daily limit. */
export const carePet = (session: Session, blockId: string, action: PetAction) =>
  call<PetState>(session, `/blocks/${blockId}/pet/care`, 'POST', { action })

/** Clears a poll's votes, for an owner reusing it. */
export const resetPollVotes = (session: Session, blockId: string) =>
  call<{ counts: Record<string, number>, total: number, closed: boolean }>(session, `/blocks/${blockId}/poll/reset`, 'POST')

/** Frees a reserved wishlist item from the owner's side. */
export const clearWishlistClaim = (session: Session, blockId: string, itemId: string) =>
  call<{ claimed: string[] }>(session, `/blocks/${blockId}/wishlist/claims/${encodeURIComponent(itemId)}`, 'DELETE')


/*  Anonymous questions  */

export const fetchQuestions = (session: Session, q: { filter: QuestionFilter, page?: number }) =>
  call<{ questions: Question[], page: PageInfo, stats: { total: number, unanswered: number, published: number } }>(
    session, `/questions?filter=${q.filter}&page=${q.page ?? 1}`,
  )

/** An empty answer clears it, and takes the question off the block with it. */
export const updateQuestion = (session: Session, id: string, patch: { answer?: string, published?: boolean }) =>
  call<Question>(session, `/questions/${id}`, 'PATCH', patch)

export const deleteQuestion = (session: Session, id: string) =>
  call<null>(session, `/questions/${id}`, 'DELETE')

/** Deletes the question and silently drops anything else its sender sends this account. */
export const muteQuestionSender = (session: Session, id: string) =>
  call<null>(session, `/questions/${id}/mute`, 'POST')

export const reportQuestion = (session: Session, id: string, reason: QuestionReportReason) =>
  call<null>(session, `/questions/${id}/report`, 'POST', { reason })

/** Asking takes no session: the person asking is anonymous by definition. */
export const askQuestion = (blockId: string, body: string) =>
  call<{ sent: true }>(null, `/blocks/${blockId}/questions`, 'POST', { body })

export const fetchAnswers = (blockId: string, page = 1) =>
  call<{ answers: PublishedAnswer[], page: PageInfo }>(null, `/blocks/${blockId}/answers?page=${page}`)


/*  Polls and wishlists, from a visitor's side. No session: see interactive.controller.js.  */

/** A pet, as this browser sees it: its state and what this browser did today. */
export const fetchPetState = (blockId: string, visitor: string) =>
  call<PetVisitorState>(null, `/blocks/${blockId}/pet?visitor=${encodeURIComponent(visitor)}`)

/** This browser's one action on a pet for today. */
export const visitPet = (blockId: string, action: PetAction, visitor: string) =>
  call<PetVisitorState>(null, `/blocks/${blockId}/pet/visit`, 'POST', { action, visitor })

export const fetchPollState = (blockId: string, voter: string) =>
  call<PollState>(null, `/blocks/${blockId}/poll?voter=${encodeURIComponent(voter)}`)

export const castPollVote = (blockId: string, optionId: string, voter: string) =>
  call<PollState>(null, `/blocks/${blockId}/poll/vote`, 'POST', { optionId, voter })

export const fetchWishlistState = (blockId: string) =>
  call<{ claimed: string[] }>(null, `/blocks/${blockId}/wishlist`)

export const claimWishlistItem = (blockId: string, itemId: string) =>
  call<{ token: string, claimed: string[] }>(null, `/blocks/${blockId}/wishlist/claim`, 'POST', { itemId })

export const releaseWishlistItem = (blockId: string, itemId: string, token: string) =>
  call<{ claimed: string[] }>(null, `/blocks/${blockId}/wishlist/release`, 'POST', { itemId, token })


/*  Manage  */

export const fetchOverview = (session: Session) =>
  call<OverviewResponse>(session, '/overview')

/** One row of an audience breakdown: a bucket and how many views it drew. */
export interface AudienceSlice {
  key: string
  views: number
}

export interface AudienceResponse {
  /** The window actually served, which a plan's retention may have shortened. */
  days: number
  totals: {
    views: number
    /** Distinct sessions. A visitor who came back four times is one. */
    visitors: number
    clicks: number
    /** Clicks per view. Can exceed 100%: one visitor may press several blocks. */
    clickRate: number
  }
  countries: AudienceSlice[]
  devices: AudienceSlice[]
  sources: AudienceSlice[]
  /** In-app browsers, read from the user agent rather than the referrer. */
  apps: AudienceSlice[]
}

export const fetchAudience = (session: Session, days = 30) =>
  call<AudienceResponse>(session, `/audience?days=${days}`)

export const fetchStats = (session: Session, days = 14) =>
  call<StatsResponse>(session, `/stats?days=${days}`)

export const fetchProducts = (session: Session) =>
  call<{ products: Product[], stats: Record<string, number> }>(session, '/products')

export const createProduct = (session: Session, body: Partial<Product>) =>
  call<Product>(session, '/products', 'POST', body)

export const updateProduct = (session: Session, id: string, body: Partial<Product>) =>
  call<Product>(session, `/products/${id}`, 'PATCH', body)

export const deleteProduct = (session: Session, id: string) =>
  call<null>(session, `/products/${id}`, 'DELETE')

/**
 * Where a table is in its own results.
 *
 * Returned alongside the rows rather than wrapped around them, so every screen
 * that already reads `data.orders` as an array keeps working.
 */
export interface PageInfo {
  page: number
  pageSize: number
  total: number
  pages: number
}

/** What a paged fetcher takes. Absent means the first page at the default size. */
export interface PageQuery { page?: number, pageSize?: number }

const pageQuery = (q?: PageQuery) => {
  const params = new URLSearchParams()
  if(q?.page) params.set('page', String(q.page))
  if(q?.pageSize) params.set('pageSize', String(q.pageSize))
  const query = params.toString()
  return query ? `?${query}` : ''
}

export const fetchOrders = (session: Session, q?: PageQuery) =>
  call<{ orders: Order[], page: PageInfo, stats: Record<string, number> }>(session, `/orders${pageQuery(q)}`)

export const setOrderStatus = (session: Session, id: string, status: Order['status']) =>
  call<Order>(session, `/orders/${id}/status`, 'PATCH', { status })

export const fetchServices = (session: Session) =>
  call<Service[]>(session, '/services')

export const createService = (session: Session, body: Partial<Service>) =>
  call<Service>(session, '/services', 'POST', body)

export const updateService = (session: Session, id: string, body: Partial<Service>) =>
  call<Service>(session, `/services/${id}`, 'PATCH', body)

export const deleteService = (session: Session, id: string) =>
  call<null>(session, `/services/${id}`, 'DELETE')

export const fetchAvailability = (session: Session, serviceId: string) =>
  call<Availability>(session, `/services/${serviceId}/availability`)

/**
 * Replaces the whole week in one write.
 *
 * A seller edits their week as a unit, and per-rule requests can land
 * half-applied, leaving hours nobody chose.
 */
export const setAvailability = (session: Session, serviceId: string, rules: AvailabilityRule[]) =>
  call<Availability>(session, `/services/${serviceId}/availability`, 'PUT', {
    rules: rules.map(({ weekday, startMinute, endMinute }) => ({ weekday, startMinute, endMinute })),
  })

export const setAvailabilityException = (
  session: Session,
  serviceId: string,
  body: { date: string, isClosed: boolean, intervals?: TimeRange[] },
) => call<Availability>(session, `/services/${serviceId}/availability/exception`, 'PUT', body)

export const clearAvailabilityException = (session: Session, serviceId: string, date: string) =>
  call<Availability>(session, `/services/${serviceId}/availability/exception?date=${encodeURIComponent(date)}`, 'DELETE')

/** The owner's own diary. Carries the client, unlike the public slot list. */
export const fetchCalendar = (session: Session, from: string, to: string, serviceId?: string) =>
  call<{ from: string, to: string, bookings: CalendarBooking[] }>(
    session,
    `/calendar?from=${from}&to=${to}${serviceId ? `&serviceId=${serviceId}` : ''}`,
  )

export const fetchBookings = (session: Session, q?: PageQuery) =>
  call<{ bookings: Booking[], page: PageInfo, stats: Record<string, number> }>(session, `/bookings${pageQuery(q)}`)

export const setBookingStatus = (session: Session, id: string, status: Booking['status']) =>
  call<Booking>(session, `/bookings/${id}/status`, 'PATCH', { status })

export const fetchDonations = (session: Session, q?: PageQuery) =>
  call<{ donations: Donation[], page: PageInfo, stats: Record<string, number> }>(session, `/donations${pageQuery(q)}`)

/**
 * Sales, filtered server-side.
 *
 * Both the search and the event filter run on the server because the table is
 * paged: narrowing the twenty rows already fetched would search a page rather
 * than the sales. The stats row is filtered by the same `eventId`, so the
 * totals above the table always describe whatever the table is showing.
 */
export const fetchTickets = (session: Session, q?: PageQuery & { search?: string, eventId?: string }) => {
  const params = new URLSearchParams()
  if(q?.page) params.set('page', String(q.page))
  if(q?.pageSize) params.set('pageSize', String(q.pageSize))
  const term = q?.search?.trim()
  if(term) params.set('search', term)
  if(q?.eventId) params.set('eventId', q.eventId)
  const query = params.toString()
  return call<{ tickets: Ticket[], page: PageInfo, stats: Record<string, number> }>(
    session,
    `/tickets${query ? `?${query}` : ''}`,
  )
}

/**
 * The event a ticket screen should open on.
 *
 * A seller looking at sales is nearly always looking at one event, and which
 * one is predictable: whatever is happening now, else whatever is next, else
 * whatever happened last. Opening on a combined total across every event they
 * have ever run answers a question nobody asked.
 *
 * Running beats upcoming because an event in progress is the one with a door
 * open and people arriving at it.
 *
 * Events with no date sort last. They cannot be reasoned about on a timeline,
 * so they are a fallback rather than a default.
 */
export function defaultEventId(events: FuseEvent[], now: Date = new Date()): string {
  if(events.length === 0) return ''

  const at = (value: string | null) => (value ? new Date(value).getTime() : null)
  const stamp = now.getTime()

  const dated = events.filter(event => at(event.startsAt) !== null)

  /* Running: started, and not yet finished.
   *
   * A missing `endsAt` means the event ends when it starts, NOT that it runs
   * indefinitely. Treating null as open-ended makes every past event without
   * an end date look like it is still going, so a show from last year would
   * shadow the one happening next week. */
  const running = [...dated]
    .filter(event => {
      const start = at(event.startsAt)!
      const end = at(event.endsAt) ?? start
      return start <= stamp && end >= stamp
    })
    .sort((left, right) => at(left.startsAt)! - at(right.startsAt)!)
  if(running.length) return running[0].id

  // Next up: the soonest start still ahead of us.
  const upcoming = [...dated]
    .filter(event => at(event.startsAt)! > stamp)
    .sort((left, right) => at(left.startsAt)! - at(right.startsAt)!)
  if(upcoming.length) return upcoming[0].id

  // Nothing ahead: the one that finished most recently.
  const past = [...dated].sort((left, right) => at(right.startsAt)! - at(left.startsAt)!)
  if(past.length) return past[0].id

  return events[0].id
}

export const fetchEvents = (session: Session) =>
  call<FuseEvent[]>(session, '/events')

export const fetchEvent = (session: Session, id: string) =>
  call<{ event: FuseEvent, ticketTypes: TicketType[] }>(session, `/events/${id}`)

export const createEvent = (session: Session, body: Partial<FuseEvent>) =>
  call<FuseEvent>(session, '/events', 'POST', body)

export const updateEvent = (session: Session, id: string, body: Partial<FuseEvent>) =>
  call<FuseEvent>(session, `/events/${id}`, 'PATCH', body)

export const deleteEvent = (session: Session, id: string) =>
  call<null>(session, `/events/${id}`, 'DELETE')

export const createTicketType = (session: Session, eventId: string, body: Partial<TicketType>) =>
  call<TicketType>(session, `/events/${eventId}/types`, 'POST', body)

export const updateTicketType = (session: Session, id: string, body: Partial<TicketType>) =>
  call<TicketType>(session, `/ticket-types/${id}`, 'PATCH', body)

export const deleteTicketType = (session: Session, id: string) =>
  call<null>(session, `/ticket-types/${id}`, 'DELETE')

/** Admits a ticket at the door. Authenticated as the event owner. */
export const checkInTicket = (session: Session, code: string) =>
  call<Ticket>(session, '/tickets/check-in', 'POST', { code })

export const fetchSubscribers = (session: Session, q?: PageQuery) =>
  call<{ subscribers: Subscriber[], page: PageInfo, stats: Record<string, number> }>(session, `/subscribers${pageQuery(q)}`)

export const removeSubscriber = (session: Session, id: string) =>
  call<null>(session, `/subscribers/${id}`, 'DELETE')

export const fetchPayout = (session: Session, q?: PageQuery) =>
  call<{ account: PayoutAccount | null, summary: Record<string, number> | null, payments: Payment[], page: PageInfo }>(session, `/payout${pageQuery(q)}`)

export const savePayoutAccount = (session: Session, body: { bankCode: string, accountNumber: string, accountIban: string, holderName: string }) =>
  call<PayoutAccount>(session, '/payout/account', 'PUT', body)

/**
 * The banks a payout can go to.
 *
 * Fetched rather than bundled: the backend validates against this same list, so
 * a copy here would eventually drift into offering a choice the server refuses.
 */
export const fetchBanks = (session: Session) =>
  call<Bank[]>(session, '/payout/banks')

/** Retries the QPay registration for an account that is already saved. */
export const retryPayoutMerchant = (session: Session) =>
  call<PayoutAccount>(session, '/payout/retry', 'POST')

export const fetchSubscription = (session: Session, q?: PageQuery) =>
  call<{ current: Entitlements, history: SubscriptionRecord[], page: PageInfo }>(session, `/subscription${pageQuery(q)}`)

export const startSubscription = (session: Session, body: { planSlug: string, months: number, couponCode?: string | null }) =>
  call<Checkout>(session, '/subscription', 'POST', body)

/**
 * What a code would take off, before anybody pays.
 *
 * A quote and nothing more: the server prices the charge again when the plan is
 * actually bought, so a code that expires between the two is refused there
 * rather than honoured because the client had already been shown a number.
 */
export const previewCoupon = (session: Session, body: { code: string, planSlug: string, months: number }) =>
  call<CouponPreview>(session, '/coupons/preview', 'POST', body)

/**
 * What a purchase would cost, priced by the server.
 *
 * The checkout does not work this out for itself. An upgrade credit depends on
 * what was paid and when, a coupon on rules the client has never seen, and a
 * pack price on a discount ladder held on the plan row. Anything computed here
 * would be a second implementation of the price, free to drift from the one
 * that charges.
 */
export interface Quote {
  subtotal: number
  discount: number
  /** A recent purchase, coming off an upgrade. Zero when extending the same plan. */
  credit: number
  total: number
  months: number
  /** True when this buys more of the plan already running, rather than a different one. */
  extending: boolean
  startsAt: string
  endsAt: string
}

export const quoteSubscription = (session: Session, body: { planSlug: string, months: number, couponCode?: string | null }) =>
  call<Quote>(session, '/subscription/quote', 'POST', body)

/** Asks whether an invoice has been paid. Polled while the QR is on screen. */
export const fetchPaymentStatus = (invoiceId: string) =>
  call<{ status: Payment['status'], paidAt: string | null }>(null, `/payments/${encodeURIComponent(invoiceId)}/status`)

export const cancelSubscription = (session: Session) =>
  call<SubscriptionRecord>(session, '/subscription/cancel', 'POST')

export const fetchAuditLog = (session: Session, q?: PageQuery) =>
  call<{ entries: AuditEntry[], page: PageInfo }>(session, `/audit${pageQuery(q)}`)

export const fetchPlans = () =>
  call<Plan[]>(null, '/plans')


/*  Account  */

/**
 * The signed-in account, re-read rather than taken from the session token.
 *
 * The token is minted at sign-in and only rotated on refresh, so it lags behind
 * anything the account has changed since — including `hasPassword`, which is
 * exactly what decides whether the security screen offers "set" or "change".
 */
export const fetchAccount = (session: Session) =>
  call<Account>(session, '/user')

/** For an account that already has a password. Verifies the current one first. */
export const changePassword = (session: Session, body: { oldPassword: string, newPassword: string }) =>
  call<Account>(session, '/user/password/change', 'POST', body)

/**
 * For an account that has none, which is every account created through Google.
 * The backend refuses if a password already exists, so this cannot overwrite
 * one from a stolen session.
 */
export const createPassword = (session: Session, body: { password: string }) =>
  call<Account>(session, '/user/password', 'POST', body)

export const saveAccountProfile = (session: Session, body: { name?: string, bio?: string }) =>
  call<Account>(session, '/user/profile', 'POST', body)

/**
 * Confirming a phone number on the account: a code by SMS, then the code back.
 * The second call is what marks the number confirmed, which is what lets it
 * sign in with a code. Its reply is the user, not the full Account, so the
 * settings screen re-reads the account afterwards.
 */
export const sendPhoneCode = (session: Session, phone: string) =>
  call<{ ok: 1 }>(session, '/user/phone', 'POST', { phone })

export const confirmPhone = (session: Session, phone: string, code: string) =>
  call<unknown>(session, '/user/phone/verify', 'POST', { phone, code })

/**
 * Deletes the signed-in account, immediately and for good.
 *
 * `confirm` is the handle as the person typed it; the backend compares it and
 * refuses a mismatch, so a stray click or a forged request cannot do this.
 * POST, not DELETE, because `sendClientRequest` sends no body with DELETE.
 *
 * Every token the account had is revoked by the time this resolves, so the
 * caller signs out locally afterwards rather than expecting the session to
 * still work.
 */
export const deleteAccount = (session: Session, body: { confirm: string }) =>
  call<{ ok: number }>(session, '/user/delete', 'POST', body)

/**
 * Opens a ДАН handshake and returns where to send the browser.
 *
 * The URL is built server-side and never here: it needs the client id and the
 * scope encoding, and a client that supplied its own `state` would be choosing
 * which verification row the callback claims.
 */
export const startDanVerification = (session: Session, body: { loginType?: string, returnTo?: string }) =>
  call<{ url: string, expiresAt: string }>(session, '/user/dan/start', 'POST', body)

export const fetchDanStatus = (session: Session) =>
  call<{ isDanVerified: boolean, danVerifiedAt: string | null, configured: boolean }>(session, '/user/dan')


/*  Shapes the loosely-typed endpoints return  */

/** A bank from the Bank of Mongolia registry, as `/payout/banks` returns it. */
export interface Bank {
  code: string
  /** Mongolian name. */
  name: string
  /** Latin name, shown in non-Mongolian locales. */
  short: string
}

/**
 * Never carries the full account number: the API returns the last four only.
 *
 * The IBAN is masked the same way, because it embeds the account number and
 * returning it whole would undo the masking beside it.
 */
export interface PayoutAccount {
  id: string
  bankCode: string | null
  bankName: string
  holderName: string
  accountLast4: string
  ibanLast4: string | null
  hasIban: boolean
  /**
   * Whether QPay has registered this seller, which is what actually switches
   * on donations, bookings, orders and tickets. A saved bank account with this
   * false takes no money: there is nowhere for it to go.
   */
  canAcceptPayments: boolean
  /** Why registration last failed, as an error code the app words itself. */
  qpayError: string | null
  verifiedAt: string | null
}

/**
 * A QPay invoice, as the checkout screen needs it.
 *
 * `qrText` is what the QR encodes; `urls` are the per-bank deeplinks, which are
 * the only way to pay on the phone that is displaying the QR. `checkoutUrl` is
 * QPay's own page, for anywhere neither of those fits.
 */
export interface Invoice {
  invoiceId: string
  qrText: string | null
  checkoutUrl: string | null
  urls: { name: string, description: string, logo: string, link: string }[]
  isStub?: boolean
}

/**
 * One payment, as the public link serves it.
 *
 * Deliberately anonymous: an amount, a reference and a way to pay. Whoever
 * holds the link can settle the bill and learn nothing about whose it is.
 */
export interface PublicPayment {
  id: string
  reference: string | null
  kind: Payment['kind']
  amount: number
  currency: string
  status: Payment['status']
  paidAt: string | null
  expiresAt: string | null
  invoice: Invoice | null
}

/** No session: the whole point is that a payer does not need one. */
export const fetchPublicPayment = (paymentId: string) =>
  call<PublicPayment>(null, `/pay/${encodeURIComponent(paymentId)}`)

/** What a visitor sends to support a page. */
export interface DonationInput {
  pageId: string
  amount: number
  message?: string
  isAnonymous?: boolean
  supporterName?: string
  /**
   * Where to send a receipt, if the supporter asked for one.
   *
   * Never reaches the page owner: the backend drops it from the donation
   * presenter, so no API response carries it.
   */
  supporterEmail?: string
}

/** What comes back: the donation row, and the invoice to pay it with. */
export interface DonationResult {
  donation: { id: string, amount: number, paidAt: string | null }
  payment: Payment | null
  invoice: Invoice | null
}

/**
 * Opens a donation and its invoice.
 *
 * No session, deliberately: a supporter is a stranger passing through, and
 * asking them to make an account first is how a donation does not happen.
 */
export const createDonation = (body: DonationInput) =>
  call<DonationResult>(null, '/checkout/donation', 'POST', body)

/** What the buyer fills in, plus what they are buying. */
export interface OrderInput {
  /**
   * Which page the order was placed from. The backend resolves the seller from
   * it, so a checkout can only ever open against the shop whose page it came
   * from and the browser is never told an account id.
   */
  pageId: string
  items: Array<{ productId: string, quantity: number }>
  buyerName?: string
  buyerPhone?: string
  buyerEmail?: string
  deliveryAddress?: string
  note?: string
  /** A preference. The backend verifies the seller actually offers it. */
  fulfilment?: 'shipping' | 'pickup'
}

/** What comes back: the order row, and the invoice to pay it with. */
export type Fulfilment = 'none' | 'shipping' | 'pickup' | 'digital'

export interface OrderResult {
  order: {
    id: string
    reference: string
    total: number
    status: string
    fulfilment: Fulfilment
    shippingFee: number
  }
  /** Where to collect. Only on a pickup order, and only in this response. */
  pickup: { address: string | null, note: string | null } | null
  payment: Payment | null
  invoice: Invoice | null
  /** Set when the order saved but the invoice could not be opened. */
  paymentError: { error: string } | null
}

/**
 * Places an order and opens its invoice.
 *
 * Unauthenticated for the same reason `createDonation` is: a buyer is a
 * stranger on somebody's page, and requiring an account before they can pay is
 * how the sale does not happen. The seller is named in the body rather than
 * inferred, because there is no session to infer them from.
 */
export const createOrder = (body: OrderInput) =>
  call<OrderResult>(null, '/checkout/order', 'POST', body)

/** One free slot, as the public calendar offers it. */
export interface Slot {
  startsAt: string
  endsAt: string
}

/**
 * The free slots for a service.
 *
 * Public and unauthenticated: this is what a visitor's calendar reads. Only
 * free times come back, never who holds the taken ones, so it cannot be used to
 * read somebody's diary.
 */
export const fetchSlots = (serviceId: string, from: string, to: string) =>
  call<Slot[]>(null, `/services/${serviceId}/slots?from=${from}&to=${to}`)

export interface BookingInput {
  serviceId: string
  /** Which page it was booked from, same reasoning as `OrderInput.pageId`. */
  pageId: string
  /** The slot, as the exact instant the slot list gave. */
  startsAt: string
  clientName?: string
  clientPhone?: string
  clientEmail?: string
  note?: string
}

export interface BookingResult {
  booking: { id: string, reference: string, startsAt: string, endsAt: string, price: number, status: string }
  payment: Payment | null
  invoice: Invoice | null
  paymentError: { error: string } | null
}

/**
 * Books a slot and opens its invoice.
 *
 * Unauthenticated for the same reason `createOrder` is: a client booking time
 * is a stranger on somebody's page, and requiring an account first is how the
 * booking does not happen.
 */
export const createBooking = (body: BookingInput) =>
  call<BookingResult>(null, '/checkout/booking', 'POST', body)

export interface TicketInput {
  ticketTypeId: string
  quantity: number
  buyerName?: string
  buyerPhone?: string
  buyerEmail?: string
}

export interface TicketResult {
  tickets: { id: string, code: string, price: number, status: string }[]
  purchaseId: string
  payment: Payment | null
  invoice: Invoice | null
  paymentError: { error: string } | null
}

/**
 * Buys tickets and opens one invoice for the lot.
 *
 * Unauthenticated, like every other public checkout: a buyer is a stranger on
 * somebody's page, and requiring an account first is how the sale does not
 * happen. Several tickets are one payment, which is why the result carries a
 * `purchaseId` rather than a ticket id.
 */
export const createTicketPurchase = (body: TicketInput) =>
  call<TicketResult>(null, '/checkout/tickets', 'POST', body)

/**
 * Uploads a product's file to private storage.
 *
 * Its own call rather than the shared `uploadFiles`, because the destination is
 * different in the way that matters: this one never reaches the public bucket.
 * The response is passed to `createProduct`/`updateProduct` as `digital`.
 */
export const uploadDigitalFile = async (file: File): Promise<DigitalFile | null> => {
  const form = new FormData()
  form.append('file', file)
  const result = await uploadFiles({ formData: form, path: '/products/file' })
  if(!result || result.status !== 200) return null
  return result.data as DigitalFile
}

/**
 * Uploads a CV's PDF.
 *
 * Answers the raw response rather than null on failure, so the editor can tell
 * "too large" from "not a PDF" and say which.
 */
export const uploadCvPdf = async (file: File) => {
  const form = new FormData()
  form.append('file', file)
  return uploadFiles({ formData: form, path: '/upload/cv-pdf' })
}

/** What a buyer sees on their download page. */
export interface DownloadInfo {
  productName: string
  fileName: string | null
  fileSize: number | null
  remaining: number
  total: number
}

export const fetchDownload = (claim: string) =>
  call<DownloadInfo>(null, `/download/${claim}`)

/** Spends one download and returns a single-use token. */
export const requestDownloadToken = (claim: string) =>
  call<{ token: string, fileName: string | null, remaining: number }>(null, `/download/${claim}/token`, 'POST')

/** The seller granting a buyer another go. */
export const grantExtraDownloads = (session: Session, grantId: string, extra = 1) =>
  call<{ remaining: number }>(session, `/downloads/${grantId}/extra`, 'POST', { extra })

/** The smallest donation the backend will open an invoice for. */
export const fetchDonationMinimum = () =>
  call<{ minimum: number }>(null, '/donations/minimum')

export interface Checkout {
  subscription: SubscriptionRecord
  payment: Payment
  /** Null when the pack cost nothing, which settles without troubling QPay. */
  invoice: Invoice | null
}

export interface CouponPreview {
  code: string
  type: 'percent' | 'fixed'
  value: number
  discount: number
  subtotal: number
  total: number
}

/** One purchasable duration of a plan, already priced by the backend. */
export interface PlanPack {
  months: number
  total: number
  listPrice: number
  saving: number
  monthly: number
  discountPercent: number
}

export interface Plan {
  id: string
  name: string
  slug: string
  description: string | null
  badges: string[]
  price: number
  packs: PlanPack[]
  canSellProducts: boolean
  canAcceptBookings: boolean
  canSellTickets: boolean
  canAcceptDonations: boolean
  canCollectEmails: boolean
  canUseThemes: boolean
  canUseCustomDomain: boolean
  canHideBranding: boolean
  canViewFullStats: boolean
  canExportData: boolean
  canInviteTeam: boolean
  canUsePremiumPets: boolean
  maxBlocks: number | null
  maxPages: number | null
  maxProducts: number | null
  /** Optional because an older server does not send it. Null is unlimited. */
  maxPosts?: number | null
}

export interface SubscriptionRecord {
  id: string
  planSlug: string | null
  planName: string | null
  months: number
  amount: number
  discount: number
  startsAt: string
  endsAt: string
  status: 'pending' | 'active' | 'grace' | 'expired' | 'cancelled'
  autoRenew: boolean
}

export interface AuditEntry {
  id: string
  action: string
  meta: Record<string, unknown>
  createdAt: string
}

/** What the overview screen aggregates, each block absent if it failed. */
export interface OverviewResponse {
  today: { views: number, clicks: number } | null
  orders: { pending: number, paid: number, delivered: number, revenue: number } | null
  bookings: { upcoming: number, today: number, cancelled: number, revenue: number } | null
  donations: { total: number, count: number, supporters: number, average: number } | null
  subscribers: { total: number, recent: number, unsubscribed: number } | null
  products: { total: number, published: number, soldout: number, stockValue: number } | null
}


/** One password rule, and whether the password under test satisfies it. */
export interface PasswordCriterion {
  id: string
  label: string
  met?: boolean
}

/**
 * The account behind the session, as `GET /user` returns it.
 *
 * A narrower thing than the profile page: this is who is signed in, not what
 * visitors see. `hasPassword` says only whether one exists.
 */
export interface Account {
  id: string
  username: string | null
  name: string | null
  bio: string | null
  /** Null for an account made by SMS. */
  email: string | null
  phone: string | null
  avatar: string | null
  locale: string | null
  status: string
  createdAt: string
  isVerified: boolean
  isPhoneVerified: boolean
  hasPassword: boolean
  /**
   * Whether ДАН has confirmed a citizen ID card for this account.
   *
   * Separate from `isVerified`, which is the contact the account signs in with
   * (its email, or its phone when it was made by SMS and has no email). The two
   * mean different things and drive different screens: that flag gates the
   * secure area, this one is what the public badge is built on.
   */
  isDanVerified: boolean
  danVerifiedAt: string | null
  /** 'person' or 'business'. A business page verifies with its owner's ID. */
  entityType: string
  providers?: string[]
}
